import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { formatDoctorName } from "../lib/displayNames";
import { Phone, MapPin, Mail, Star, User, Activity, ArrowRight, Menu, X, CheckCircle2, Calendar } from "lucide-react";
import PublicBooking from "./PublicBooking";

export function PublicWebsite() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [selectedSrvId, setSelectedSrvId] = useState(null);
  const [doctorModalOpen, setDoctorModalOpen] = useState(null);

  const handleBookWithDoctor = (docId) => {
    setSelectedDocId(docId);
    setSelectedSrvId(null);
    setDoctorModalOpen(null);
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleBookWithService = (srvId) => {
    setSelectedSrvId(srvId);
    setSelectedDocId(null);
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    api.get(`/public/website/${slug}`)
       .then(r => { setData(r.data); setLoading(false); })
       .catch(e => { setLoading(false); });
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center">Clinic not found.</div>;

  const { clinic, mode } = data;

  if (mode === "basic") {
     return <PublicBooking />;
  }

  const { website, pages, doctors, services, faqs, testimonials, gallery = [] } = data;

  const theme = {
     "--p": website.primary_color,
     "--s": website.secondary_color,
     "--a": website.accent_color,
     "--b": website.background_color,
     "--t": website.text_color,
  };

  const getPage = (key) => pages.find(p => p.page_key === key);
  const hasPage = (key) => !!getPage(key);
  const pageContent = (key) => getPage(key)?.content_json || {};
  const home = pageContent("home");
  const about = pageContent("about");
  const servicesC = pageContent("services");
  const doctorsC = pageContent("doctors");
  const testimonialsC = pageContent("testimonials");
  const aboutValues = (about.values && about.values.length > 0) ? about.values : [
    { title: "Expert Doctors", desc: "Highly qualified medical professionals" },
    { title: "Advanced Care", desc: "Modern facilities and equipment" },
    { title: "24/7 Support", desc: "Always here when you need us" },
    { title: "Easy Booking", desc: "Seamless online appointments" },
  ];
  const valueIcons = [<User className="w-6 h-6"/>, <Activity className="w-6 h-6"/>, <Phone className="w-6 h-6"/>, <Calendar className="w-6 h-6"/>];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  return (
    <div className="min-h-screen font-sans bg-slate-50" style={{...theme, color: 'var(--t)'}}>
       {/* Top Contact Bar */}
       <div className="hidden md:block text-white text-xs py-2" style={{background: 'var(--s)'}}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
             <div className="flex gap-6 opacity-90">
                {clinic.phone && <span className="flex items-center gap-1.5"><Phone className="w-3 h-3"/> {clinic.phone}</span>}
                {clinic.email && <span className="flex items-center gap-1.5"><Mail className="w-3 h-3"/> {clinic.email}</span>}
                {clinic.address && <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3"/> {clinic.address}</span>}
             </div>
             <div className="flex gap-4 opacity-90">
                {/* Social icons placeholder */}
                <span>Opening Hours: Mon-Sat, 9AM-8PM</span>
             </div>
          </div>
       </div>

       {/* Header */}
       <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b shadow-sm transition-all" style={{borderColor: 'var(--p)20'}}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
             <div className="flex justify-between items-center h-20">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-3"
                >
                   <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg" style={{background: 'linear-gradient(135deg, var(--p) 0%, var(--s) 100%)'}}>
                      {clinic.name.charAt(0)}
                   </div>
                   <h1 className="text-xl font-bold bg-clip-text text-transparent" style={{backgroundImage: 'linear-gradient(to right, var(--p), var(--s))'}}>{clinic.name}</h1>
                </motion.div>
                
                {/* Desktop Nav */}
                <motion.nav 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="hidden md:flex gap-8 items-center"
                >
                   {hasPage("home") && <a href={`#home`} className="font-semibold transition-colors hover:opacity-70" style={{color: 'var(--s)'}}>Home</a>}
                   {hasPage("about") && <a href={`#about`} className="font-semibold transition-colors hover:opacity-70" style={{color: 'var(--s)'}}>About</a>}
                   {hasPage("services") && <a href={`#services`} className="font-semibold transition-colors hover:opacity-70" style={{color: 'var(--s)'}}>Services</a>}
                   {hasPage("doctors") && <a href={`#doctors`} className="font-semibold transition-colors hover:opacity-70" style={{color: 'var(--s)'}}>Doctors</a>}
                   {gallery.length > 0 && <a href={`#gallery`} className="font-semibold transition-colors hover:opacity-70" style={{color: 'var(--s)'}}>Gallery</a>}
                   <a href="#book" className="px-6 py-2.5 rounded-full text-white font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" style={{background: 'linear-gradient(to right, var(--a), var(--p))'}}>
                      Book Appointment
                   </a>
                </motion.nav>

                {/* Mobile menu button */}
                <button className="md:hidden p-2 rounded-lg" style={{color: 'var(--s)'}} onClick={() => setMenuOpen(!menuOpen)}>
                   {menuOpen ? <X /> : <Menu />}
                </button>
             </div>
          </div>
       </header>

       {/* Mobile Nav */}
       {menuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden bg-white/90 backdrop-blur-xl border-b px-4 py-4 space-y-4 shadow-lg absolute w-full z-40"
          >
             <a href="#home" onClick={()=>setMenuOpen(false)} className="block font-bold p-2 rounded-lg hover:bg-slate-50">Home</a>
             <a href="#about" onClick={()=>setMenuOpen(false)} className="block font-bold p-2 rounded-lg hover:bg-slate-50">About</a>
             <a href="#services" onClick={()=>setMenuOpen(false)} className="block font-bold p-2 rounded-lg hover:bg-slate-50">Services</a>
             <a href="#doctors" onClick={()=>setMenuOpen(false)} className="block font-bold p-2 rounded-lg hover:bg-slate-50">Doctors</a>
             <a href="#book" onClick={()=>setMenuOpen(false)} className="block text-center px-6 py-3 rounded-xl text-white font-bold mt-4 shadow-lg" style={{background: 'linear-gradient(to right, var(--a), var(--p))'}}>Book Appointment</a>
          </motion.div>
       )}

       {/* Hero Section */}
       <section id="home" className="relative pt-24 pb-32 lg:pt-36 lg:pb-48 overflow-hidden flex items-center justify-center min-h-[85vh]">
          {/* Animated Background blobs */}
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-white rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob" style={{backgroundColor: 'var(--p)'}}></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob animation-delay-2000" style={{backgroundColor: 'var(--a)'}}></div>
          <div className="absolute -bottom-32 left-1/2 w-[500px] h-[500px] bg-white rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob animation-delay-4000" style={{backgroundColor: 'var(--s)'}}></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
             <motion.div 
               variants={staggerContainer}
               initial="hidden"
               animate="visible"
               className="max-w-4xl mx-auto flex flex-col items-center"
             >
                <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-sm border border-white/40 shadow-sm mb-8">
                  <span className="w-2 h-2 rounded-full" style={{backgroundColor: 'var(--a)'}}></span>
                  <span className="text-sm font-semibold tracking-wide" style={{color: 'var(--s)'}} data-testid="hero-badge-text">{home.hero_badge_text || `Welcome to ${clinic.name}`}</span>
                </motion.div>
                
                <motion.h1 variants={fadeIn} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight mb-8 leading-tight" style={{color: 'var(--s)'}} data-testid="hero-title">
                   {home.hero_title ? home.hero_title : (
                     <>Advanced Healthcare <br/> <span className="bg-clip-text text-transparent" style={{backgroundImage: 'linear-gradient(to right, var(--p), var(--s))'}}>Tailored for You</span></>
                   )}
                </motion.h1>
                
                <motion.p variants={fadeIn} className="text-xl sm:text-2xl mb-12 opacity-80 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
                   {home.hero_subtitle || clinic.description || "Experience world-class medical care with our expert team of doctors. Book your appointment online today."}
                </motion.p>
                
                <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                   <a href="#book" className="px-8 py-4 rounded-xl text-white font-bold text-lg shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 w-full sm:w-auto" style={{background: 'linear-gradient(135deg, var(--a) 0%, var(--p) 100%)'}}>
                      {home.cta_primary_label || "Book Online Now"}
                   </a>
                   <a href="#services" className="px-8 py-4 rounded-xl font-bold text-lg bg-white/80 backdrop-blur-md shadow-lg border border-white/40 hover:bg-white hover:-translate-y-1 transition-all w-full sm:w-auto" style={{color: 'var(--p)'}}>
                      {home.cta_secondary_label || "Our Services"}
                   </a>
                </motion.div>
             </motion.div>
          </div>
       </section>

       {/* About / Core Values Section */}
       <section id="about" className="py-16 relative bg-white border-y border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
             {(about.heading || about.body) && (
               <motion.div
                 initial="hidden"
                 whileInView="visible"
                 viewport={{ once: true, margin: "-100px" }}
                 variants={fadeIn}
                 className="text-center mb-12 max-w-3xl mx-auto"
               >
                  {about.heading && <h2 className="text-3xl font-extrabold mb-3" style={{color: 'var(--s)'}} data-testid="about-heading">{about.heading}</h2>}
                  {about.subheading && <p className="font-semibold mb-3" style={{color: 'var(--p)'}}>{about.subheading}</p>}
                  {about.body && <p className="text-slate-500 leading-relaxed" data-testid="about-body">{about.body}</p>}
               </motion.div>
             )}
             <motion.div 
               variants={staggerContainer}
               initial="hidden"
               whileInView="visible"
               viewport={{ once: true, margin: "-50px" }}
               className="grid grid-cols-1 md:grid-cols-4 gap-8"
             >
                {aboutValues.map((val, i) => (
                  <motion.div variants={fadeIn} key={i} className="flex flex-col items-center text-center p-6 rounded-2xl hover:bg-slate-50 transition-colors">
                     <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{background: 'linear-gradient(135deg, var(--p)15, var(--s)15)', color: 'var(--p)'}}>
                        {valueIcons[i % valueIcons.length]}
                     </div>
                     <h3 className="text-lg font-bold mb-2" style={{color: 'var(--s)'}}>{val.title}</h3>
                     <p className="text-sm text-slate-500">{val.desc}</p>
                  </motion.div>
                ))}
             </motion.div>
          </div>
       </section>

       {/* Clinic Statistics Section */}
       <section className="py-20 relative text-white" style={{background: 'linear-gradient(to right, var(--s), var(--p))'}}>
          <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
             <motion.div 
               variants={staggerContainer}
               initial="hidden"
               whileInView="visible"
               viewport={{ once: true }}
               className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-white/20"
             >
                <motion.div variants={fadeIn} className="text-center px-4">
                   <div className="text-4xl lg:text-5xl font-extrabold mb-2">{doctors.length}+</div>
                   <div className="text-sm font-semibold tracking-wide uppercase opacity-80">Specialists</div>
                </motion.div>
                <motion.div variants={fadeIn} className="text-center px-4">
                   <div className="text-4xl lg:text-5xl font-extrabold mb-2">{services.length}+</div>
                   <div className="text-sm font-semibold tracking-wide uppercase opacity-80">Services</div>
                </motion.div>
                <motion.div variants={fadeIn} className="text-center px-4">
                   <div className="text-4xl lg:text-5xl font-extrabold mb-2">{home.stat_years_experience || "15+"}</div>
                   <div className="text-sm font-semibold tracking-wide uppercase opacity-80">Years Exp.</div>
                </motion.div>
                <motion.div variants={fadeIn} className="text-center px-4">
                   <div className="text-4xl lg:text-5xl font-extrabold mb-2">{home.stat_happy_patients || "10k+"}</div>
                   <div className="text-sm font-semibold tracking-wide uppercase opacity-80">Happy Patients</div>
                </motion.div>
             </motion.div>
          </div>
       </section>

       {/* Services Section */}
       {hasPage("services") && services.length > 0 && (
          <section id="services" className="py-24 relative bg-white/40">
             <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div 
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={fadeIn}
                  className="text-center mb-16"
                >
                   <h2 className="text-4xl font-extrabold mb-4" style={{color: 'var(--s)'}} data-testid="services-heading">{servicesC.heading || "Our Medical Services"}</h2>
                   {servicesC.subheading && <p className="text-slate-500 mb-4">{servicesC.subheading}</p>}
                   <div className="w-24 h-1.5 mx-auto rounded-full" style={{background: 'linear-gradient(to right, var(--p), var(--a))'}}></div>
                </motion.div>
                
                <motion.div 
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-8"
                >
                   {services.map(s => (
                      <motion.div variants={fadeIn} key={s.service_id} className="bg-white rounded-3xl p-8 shadow-xl shadow-slate-200/50 hover:shadow-2xl transition-all hover:-translate-y-2 border border-slate-100 group">
                         <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8 shadow-inner group-hover:scale-110 transition-transform" style={{background: `linear-gradient(135deg, var(--p)20 0%, var(--s)10 100%)`, color: 'var(--p)'}}>
                            <Activity className="w-8 h-8" />
                         </div>
                         <h3 className="text-2xl font-bold mb-4" style={{color: 'var(--s)'}}>{s.name}</h3>
                         <p className="text-slate-500 mb-8 line-clamp-3 leading-relaxed text-lg">{s.description || "Professional medical consultation and personalized treatment plan tailored to your specific health needs."}</p>
                         <div className="flex justify-between items-center mt-auto pt-6 border-t border-slate-100/80 mb-4">
                            <span className="font-medium flex items-center gap-2 text-slate-400 bg-slate-50 px-3 py-1 rounded-lg"><CheckCircle2 className="w-4 h-4"/> {s.duration_minutes} mins</span>
                            {s.fee > 0 && <span className="font-bold text-xl bg-clip-text text-transparent" style={{backgroundImage: 'linear-gradient(to right, var(--p), var(--s))'}}>₹{s.fee}</span>}
                         </div>
                         <button onClick={() => handleBookWithService(s.service_id)} className="w-full py-3 rounded-xl font-bold text-sm text-center border-2 transition-all hover:bg-slate-50" style={{borderColor: 'var(--p)', color: 'var(--p)'}}>
                            Book This Service
                         </button>
                      </motion.div>
                   ))}
                </motion.div>
             </div>
          </section>
       )}

       {/* Doctors Section */}
       {hasPage("doctors") && doctors.length > 0 && (
          <section id="doctors" className="py-24 bg-white relative">
             <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <motion.div 
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={fadeIn}
                  className="text-center mb-16"
                >
                   <h2 className="text-4xl font-extrabold mb-4" style={{color: 'var(--s)'}} data-testid="doctors-heading">{doctorsC.heading || "Meet Our Specialists"}</h2>
                   {doctorsC.subheading && <p className="text-slate-500 mb-4">{doctorsC.subheading}</p>}
                   <div className="w-24 h-1.5 mx-auto rounded-full" style={{background: 'linear-gradient(to right, var(--p), var(--a))'}}></div>
                </motion.div>
                <motion.div 
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
                >
                   {doctors.map(d => (
                      <motion.div variants={fadeIn} key={d.doctor_id} className="bg-white rounded-3xl overflow-hidden shadow-lg shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 hover:shadow-2xl transition-all group">
                         <div className="aspect-[4/4] flex items-center justify-center relative overflow-hidden group-hover:scale-105 transition-transform duration-500" style={{background: 'linear-gradient(to bottom right, #f8fafc, #f1f5f9)'}}>
                            <User className="w-24 h-24 text-slate-300 drop-shadow-md" />
                            <div className="absolute inset-0 opacity-40 mix-blend-overlay" style={{background: `linear-gradient(to top, var(--p), transparent)`}}></div>
                         </div>
                         <div className="p-8 text-center bg-white relative z-10">
                            <h3 className="text-xl font-bold mb-1" style={{color: 'var(--s)'}}>{formatDoctorName(d.name, d.salutation)}</h3>
                            <p className="text-sm font-bold mb-4 tracking-wide uppercase" style={{color: 'var(--p)'}}>{d.specialization}</p>
                            <p className="text-sm text-slate-500 mb-6 font-medium bg-slate-50 py-1.5 px-3 rounded-lg inline-block">{d.qualification}</p>
                            <div className="flex gap-2">
                               <button onClick={() => setDoctorModalOpen(d)} className="flex-1 py-2 rounded-lg font-bold text-sm bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors">
                                  View Profile
                               </button>
                               <button onClick={() => handleBookWithDoctor(d.doctor_id)} className="flex-1 py-2 rounded-lg font-bold text-sm text-white shadow-md hover:shadow-lg transition-all" style={{background: 'linear-gradient(to right, var(--p), var(--s))'}}>
                                  Book
                               </button>
                            </div>
                         </div>
                      </motion.div>
                   ))}
                </motion.div>
             </div>
          </section>
       )}

       {/* Gallery Section */}
       {gallery.length > 0 && (
          <section id="gallery" className="py-24 relative bg-white/40">
             <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={fadeIn}
                  className="text-center mb-16"
                >
                   <h2 className="text-4xl font-extrabold mb-4" style={{color: 'var(--s)'}} data-testid="gallery-heading">Clinic Gallery</h2>
                   <div className="w-24 h-1.5 mx-auto rounded-full" style={{background: 'linear-gradient(to right, var(--p), var(--a))'}}></div>
                </motion.div>
                <motion.div
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  className="grid grid-cols-2 md:grid-cols-4 gap-4"
                >
                   {gallery.map((img) => (
                      <motion.div variants={fadeIn} key={img.image_id} className="rounded-2xl overflow-hidden shadow-lg aspect-square group relative" data-testid={`gallery-image-${img.image_id}`}>
                         <img src={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${img.image_path}`} alt={img.caption || "Clinic"} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                         {img.caption && (
                           <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity">{img.caption}</div>
                         )}
                      </motion.div>
                   ))}
                </motion.div>
             </div>
          </section>
       )}

       {/* Testimonials */}
       {hasPage("testimonials") && testimonials.length > 0 && (
          <section className="py-24 text-white relative overflow-hidden">
             <div className="absolute inset-0" style={{background: 'linear-gradient(135deg, var(--s) 0%, var(--p) 100%)'}}></div>
             <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
             <div className="max-w-7xl mx-auto px-4 relative z-10">
                <motion.div 
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  className="text-center mb-16"
                >
                   <h2 className="text-4xl font-extrabold mb-4">{testimonialsC.heading || "Patient Stories"}</h2>
                   <div className="w-24 h-1.5 mx-auto rounded-full" style={{backgroundColor: 'var(--a)'}}></div>
                </motion.div>
                <motion.div 
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-8"
                >
                   {testimonials.slice(0,3).map(t => (
                      <motion.div variants={fadeIn} key={t.testimonial_id} className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 hover:bg-white/20 transition-colors shadow-2xl">
                         <div className="flex gap-1.5 mb-6 text-yellow-400 drop-shadow-md">
                            {[...Array(t.rating||5)].map((_,i) => <Star key={i} className="w-5 h-5 fill-current" />)}
                         </div>
                         <p className="text-lg italic mb-8 leading-relaxed text-white/90">"{t.comment}"</p>
                         <div className="font-bold tracking-wide flex items-center gap-3">
                           <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                             <User className="w-5 h-5 text-white" />
                           </div>
                           — {t.patient_display_name}
                         </div>
                      </motion.div>
                   ))}
                </motion.div>
             </div>
          </section>
       )}

       {/* Doctor Profile Modal */}
       {doctorModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
             <motion.div 
               initial={{ opacity: 0, scale: 0.95 }}
               animate={{ opacity: 1, scale: 1 }}
               className="bg-white rounded-3xl overflow-hidden max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl"
             >
                <div className="flex justify-between items-center p-6 border-b border-slate-100">
                   <h3 className="text-2xl font-bold" style={{color: 'var(--s)'}}>Doctor Profile</h3>
                   <button onClick={() => setDoctorModalOpen(null)} className="p-2 hover:bg-slate-100 rounded-full transition-colors" aria-label="Close doctor details"><X className="w-6 h-6 text-slate-500" aria-hidden="true"/></button>
                </div>
                <div className="p-6 md:p-8 overflow-y-auto flex-1 flex flex-col md:flex-row gap-8">
                   <div className="w-full md:w-1/3 flex flex-col items-center">
                      <div className="w-40 h-40 rounded-3xl bg-slate-100 flex items-center justify-center mb-6 shadow-inner" style={{background: 'linear-gradient(to bottom right, #f8fafc, #e2e8f0)'}}>
                         <User className="w-20 h-20 text-slate-300" />
                      </div>
                      <button onClick={() => handleBookWithDoctor(doctorModalOpen.doctor_id)} className="w-full py-3 rounded-xl font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all" style={{background: 'linear-gradient(135deg, var(--p), var(--s))'}}>
                         Book Appointment
                      </button>
                   </div>
                   <div className="flex-1">
                      <h2 className="text-3xl font-extrabold mb-2" style={{color: 'var(--s)'}}>{formatDoctorName(doctorModalOpen.name, doctorModalOpen.salutation)}</h2>
                      <div className="text-lg font-bold tracking-wide uppercase mb-4" style={{color: 'var(--p)'}}>{doctorModalOpen.specialization}</div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-8">
                         <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                            <div className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Qualification</div>
                            <div className="font-semibold text-slate-700">{doctorModalOpen.qualification || "—"}</div>
                         </div>
                         <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                            <div className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Experience</div>
                            <div className="font-semibold text-slate-700">{doctorModalOpen.experience_years ? `${doctorModalOpen.experience_years} Years` : "—"}</div>
                         </div>
                      </div>

                      <div className="mb-6">
                         <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">About the Doctor</h4>
                         <p className="text-slate-600 leading-relaxed">
                            {formatDoctorName(doctorModalOpen.name, doctorModalOpen.salutation)} is a highly skilled and experienced {doctorModalOpen.specialization} specialist dedicated to providing compassionate and comprehensive care to all patients.
                         </p>
                      </div>
                   </div>
                </div>
             </motion.div>
          </div>
       )}

       {/* Booking Section embedded */}
       {hasPage("book") && (
          <section id="book" className="py-24 relative bg-slate-50 overflow-hidden">
             <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white rounded-full mix-blend-multiply filter blur-[120px] opacity-60 pointer-events-none" style={{backgroundColor: 'var(--p)'}}></div>
             <div className="max-w-5xl mx-auto px-4 relative z-10">
                <motion.div 
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  className="bg-white/80 backdrop-blur-2xl rounded-[3rem] shadow-2xl overflow-hidden border border-white p-4 sm:p-12"
                >
                   <div className="text-center mb-12">
                      <h2 className="text-4xl font-extrabold mb-4 bg-clip-text text-transparent" style={{backgroundImage: 'linear-gradient(to right, var(--s), var(--p))'}}>Book Your Appointment</h2>
                      <p className="text-slate-500 text-lg">Select your preferred service and doctor to schedule a visit.</p>
                   </div>
                   <div className="relative bg-white rounded-2xl p-4 sm:p-8 shadow-inner border border-slate-50">
                      <PublicBooking embedded={true} preselectedDoctorId={selectedDocId} preselectedServiceId={selectedSrvId} />
                   </div>
                </motion.div>
             </div>
          </section>
       )}

       {/* Footer */}
       <footer className="pt-20 pb-10" style={{backgroundColor: 'var(--s)', color: 'white'}}>
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
             <div>
                <div className="flex items-center gap-3 mb-6">
                   <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xl" style={{backgroundColor: 'var(--p)', color: 'white'}}>
                      {clinic.name.charAt(0)}
                   </div>
                   <h2 className="text-2xl font-bold">{clinic.name}</h2>
                </div>
                <p className="opacity-70 mb-6">{clinic.description}</p>
             </div>
             <div>
                <h3 className="text-lg font-bold mb-6">Contact Us</h3>
                <div className="space-y-4 opacity-80">
                   <div className="flex items-start gap-3"><MapPin className="w-5 h-5 shrink-0" /> <span>{clinic.address || "Address not provided"}</span></div>
                   <div className="flex items-center gap-3"><Phone className="w-5 h-5 shrink-0" /> <span>{clinic.phone || "Phone not provided"}</span></div>
                   {clinic.email && <div className="flex items-center gap-3"><Mail className="w-5 h-5 shrink-0" /> <span>{clinic.email}</span></div>}
                </div>
             </div>
             <div>
                <h3 className="text-lg font-bold mb-6">Quick Links</h3>
                <div className="space-y-2 opacity-80 flex flex-col">
                   <a href="#home" className="hover:opacity-100 transition-opacity">Home</a>
                   <a href="#services" className="hover:opacity-100 transition-opacity">Services</a>
                   <a href="#doctors" className="hover:opacity-100 transition-opacity">Doctors</a>
                   <a href="#book" className="hover:opacity-100 transition-opacity">Book Appointment</a>
                </div>
             </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 pt-8 border-t border-white/10 text-center opacity-50 text-sm flex flex-col md:flex-row justify-between items-center gap-4">
             <p>© {new Date().getFullYear()} {clinic.name}. All rights reserved.</p>
             <p>Powered by <span className="font-bold">MediGenie</span> Platform</p>
          </div>
       </footer>

    </div>
  );
}
